import { useCallback, useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Calls matching a filter, newest first. `symbol` resolves server-side to
 * its canonical identity, so asking for LXS returns the LNXSY history.
 */
export const useCalls = ({ status, kind, symbol, limit } = {}) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiCall(API_CONFIG.endpoints.calls({ status, kind, symbol, limit }))
      .then((data) => { if (!cancelled) setCalls(Array.isArray(data) ? data : []); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load calls', err);
        setError('Failed to load calls');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, kind, symbol, limit, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { calls, loading, error, reload };
};

/** Aggregate stats over the same filter. */
export const useScorecardSummary = ({ kind, symbol } = {}) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiCall(API_CONFIG.endpoints.callsSummary({ kind, symbol }))
      .then((data) => { if (!cancelled) setSummary(data || null); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load scorecard summary', err);
        setError('Failed to load scorecard');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, symbol, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { summary, loading, error, reload };
};

/** POST /calls/evaluate -- returns the evaluation summary. */
export const evaluateCalls = (symbol) =>
  apiCall(API_CONFIG.endpoints.callsEvaluate(symbol), { method: 'POST' });

/** POST /calls/rebuild -- rebuild from stored plans and reports, then evaluate. */
export const rebuildCalls = (reset = false) =>
  apiCall(API_CONFIG.endpoints.callsRebuild(reset), { method: 'POST' });
